import { useEffect, useMemo, useRef, useState } from "react";

export interface PickOption { value: string; label: string; detail?: string; group?: string }

/**
 * Full-height, finger-sized list to pick one value (phase code, SOV item). Search box on top,
 * group chips, big rows. Opens from a button so phones don't get a 90-row <select>.
 */
export function PickerSheet({ title, options, value, onPick, onClose, noneLabel }: {
  title: string; options: PickOption[]; value?: string; onPick: (value: string | undefined) => void; onClose: () => void; noneLabel?: string;
}) {
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("");
  const list = useRef<HTMLDivElement>(null);
  const groups = useMemo(() => [...new Set(options.map((o) => o.group).filter(Boolean) as string[])], [options]);
  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return options.filter((o) => (!group || o.group === group) && words.every((w) => `${o.label} ${o.detail ?? ""} ${o.group ?? ""}`.toLowerCase().includes(w)));
  }, [options, q, group]);
  useEffect(() => { list.current?.querySelector<HTMLElement>(".picked")?.scrollIntoView({ block: "center" }); }, []);
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; addEventListener("keydown", k); return () => removeEventListener("keydown", k); }, [onClose]);
  return (
    <div className="modal-backdrop picker-backdrop" onClick={onClose}>
      <div className="picker-sheet card" role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="row between"><h3>{title}</h3><button type="button" onClick={onClose}>Close</button></div>
        <input type="search" placeholder="Search code or name…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
        {groups.length > 1 && <div className="picker-chips">
          <button type="button" className={!group ? "on" : ""} onClick={() => setGroup("")}>All</button>
          {groups.map((g) => <button type="button" key={g} className={group === g ? "on" : ""} onClick={() => setGroup(g)}>{g}</button>)}
        </div>}
        <div className="picker-list" ref={list}>
          {noneLabel && !q && <button type="button" className={`picker-row ${!value ? "picked" : ""}`} onClick={() => onPick(undefined)}><b>{noneLabel}</b></button>}
          {shown.map((o) => (
            <button type="button" key={o.value} className={`picker-row ${o.value === value ? "picked" : ""}`} onClick={() => onPick(o.value)}>
              <b>{o.label}</b>{o.detail && <span className="muted small">{o.detail}</span>}
            </button>
          ))}
          {!shown.length && <p className="muted">Nothing matches “{q}”.</p>}
        </div>
      </div>
    </div>
  );
}

/** The button that shows the current pick and opens the sheet. */
export function PickButton({ label, text, placeholder, onOpen, disabled }: { label: string; text: string; placeholder: string; onOpen: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={`pick-button ${text ? "" : "empty"}`} aria-label={label} disabled={disabled} onClick={onOpen}>
      <span>{text || placeholder}</span><span aria-hidden>▾</span>
    </button>
  );
}
